<ng-template
  *ngIf="configuration"
  [appPermissionCheck]="root.dto.$dtoType.read"
  [appPermissionCheckVarDict]="{ $dtoType: configuration.type }"
  [appPermissionCheckElse]="hasNoPermission"
>
  <!-- We already have a global confirmDialog in the root component. For some reason this is not reachable for the inline edit cells and we need to declare another one here... -->
  <p-confirmDialog acceptLabel="Ja" rejectLabel="Nein" key="global"></p-confirmDialog>

  <p-table
    #dt
    [columns]="visibleFields"
    [value]="entityData.data"
    dataKey="id"
    [selectionMode]="tableData.showSelectList ? 'multiple' : 'single'"
    [(selection)]="selectedEntities"
    (selectionChange)="rowSelect()"
    (onRowReorder)="rowReorder($event)"
    (onLazyLoad)="loadLazyDebounce($event)"
    [paginator]="tableData.pagination"
    [rows]="configuration.rowsPerPage"
    [rowsPerPageOptions]="rowsPerPageOptions"
    [lazy]="true"
    [totalRecords]="entityData.count"
    [rowTrackBy]="trackByFunction"
    [scrollable]="tableData.scrollable || inHorizontalScroll"
    [scrollHeight]="tableData.scrollHeight"
    [scrollDirection]="inHorizontalScroll ? 'both' : 'vertical'"
    [showCurrentPageReport]="!!entityData.count"
    [currentPageReportTemplate]="(loading ? 'Lade' : 'Zeige') + ' Einträge {first}-{last} von {totalRecords}'"
    styleClass="p-datatable-striped p-datatable-gridlines white-background"
    [class.no-vertical-scroll]="!tableData.scrollable"
    (window:resize)="onTableResize()"
    [style.pointer-events]="loading ? 'none' : 'inherit'"
    jmlLibInitCallback
    (init)="afterTableInit()"
    [attr.data-cy]="'dynTable_' + configuration?.type"
    [stateKey]="tableStateKey$ | async"
    stateStorage="session"
  >
    <ng-template *ngIf="tableData.showHeader" pTemplate="caption">
      <div class="text-xl h-2rem flex align-items-center jml-datatable-header">
        <!-- icon & title for the table-->
        <i
          *ngIf="findIconForEntityType(configuration?.type)"
          [class]="findIconForEntityType(configuration?.type)"
          class="mr-2"
        >
        </i>
        <span>
          {{ configuration.displayName }}
        </span>

        <!-- favorite icon (clickable) -->
        <div class="fav-icon ml-auto">
          <i
            *ngIf="configuration"
            [class]="isFavorite(configuration.type) ? 'pi pi-star-fill' : 'pi pi-star'"
            class="favorite-icon"
            [pTooltip]="isFavorite(configuration.type) ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'"
            tooltipPosition="left"
            (click)="toggleFavorite($event)"
          >
          </i>
        </div>
      </div>
    </ng-template>

    <ng-template pTemplate="emptymessage" let-columns>
      <tr *ngIf="initialLoadCompleted; else skeletonLoader">
        <td [attr.colspan]="columns.length + (tableData.showButtons && crudColumnSpace !== 0 ? 1 : 0)">
          <div class="flex flex-column align-items-center justify-content-center h-6rem text-color-secondary">
            <i class="pi pi-info-circle" class="text-2xl"></i>
            <span>No records found</span>
          </div>
        </td>
      </tr>
      <ng-template #skeletonLoader>
        <tr *ngFor="let _ of [].constructor(configuration.rowsPerPage)">
          <td
            *ngFor="let col of columns"
            style="padding-top: 0.7rem; padding-bottom: 0.7rem"
            [style.flex]="
              !inHorizontalScroll && tableData.scrollable && columnWidths ? '0 0 ' + columnWidths[col.field] : undefined
            "
            [style.width]="columnWidths ? columnWidths[col.field] : undefined"
          >
            <div style="width: 100%; height: 20px" class="p-skeleton"></div>
          </td>
          <td *ngIf="tableData.showButtons" style="padding-top: 0.7rem; padding-bottom: 0.7rem">
            <div style="width: 100%; height: 20px" class="p-skeleton"></div>
          </td>
        </tr>
      </ng-template>
    </ng-template>

    <!-- Table footer with optional data -->
    <ng-template pTemplate="footer" let-columns *ngIf="configuration.footerFields">
      <tr>
        <td
          *ngFor="let col of columns"
          valign="top"
          [pSortableColumn]="col.sortable ? col.field : null"
          class="border-top-3"
          [ngSwitch]="col.filterType"
        >
          <div *ngFor="let fField of configuration.footerFields" [ngSwitch]="col.type">
            <ng-container *ngIf="fField.field === col.field">
              <jml-lib-string-cell
                *ngSwitchCase="'String'"
                [fieldName]="col.field"
                [rowData]="entityData.footer"
                [editable]="false"
              ></jml-lib-string-cell>
              <jml-lib-integer-cell
                *ngSwitchCase="'int'"
                [fieldName]="col.field"
                [rowData]="entityData.footer"
                [editable]="false"
              ></jml-lib-integer-cell>
              <jml-lib-float-cell
                *ngSwitchCase="'float'"
                [fieldName]="col.field"
                [rowData]="entityData.footer"
                [editable]="false"
                [decimals]="col.decimals"
              ></jml-lib-float-cell>
              <jml-lib-bool-cell
                *ngSwitchCase="'boolean'"
                [fieldName]="col.field"
                [rowData]="entityData.footer"
                [editable]="false"
              ></jml-lib-bool-cell>
              <jml-lib-currency-cell
                *ngSwitchCase="'Currency'"
                [fieldName]="col.field"
                [rowData]="entityData.footer"
                [editable]="false"
                [currency]="currency"
              ></jml-lib-currency-cell>
            </ng-container>
          </div>
        </td>
        <!-- extra cell if buttons are enabled -->
        <td *ngIf="tableData.showButtons && crudColumnSpace !== 0" class="border-top-3"></td>
      </tr>
    </ng-template>

    <!-- Table Headers -->
    <ng-template pTemplate="header" let-columns>
      <tr>
        <!-- show selectList -->
        <th [style.width.px]="'40'" *ngIf="tableData.showSelectList">
          <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
        </th>
        <th
          #tableHeader
          *ngFor="let col of columns"
          valign="top"
          [style.min-width]="
            (inHorizontalScroll || !tableData.scrollable) && columnWidths ? columnWidths[col.field] : undefined
          "
          [style.flex]="
            !inHorizontalScroll && tableData.scrollable && columnWidths ? '0 0 ' + columnWidths[col.field] : undefined
          "
          [style.max-width]="columnWidths ? columnWidths[col.field] : undefined"
          [style.width]="columnWidths ? columnWidths[col.field] : undefined"
          [ngSwitch]="col.filterType"
        >
          <div class="flex flex-column h-full">
            <ng-container [ngSwitch]="col.field">
              <!-- for field "isSelected" select all / unselect all buttons should be rendered in the header -->
              <!-- TODO: tableState.qualifier for some reason changes from undefined to ''. This leads to ExpressionChangedAfterItHasBeenCheckedError.
                         I do not know how to prevent this.
                    -->
              <jml-lib-selection-filter
                *ngSwitchCase="'isSelected'"
                [dtoType]="configuration.type"
                [mainId]="mainId"
                [ownerId]="ownerId"
                [ownerType]="ownerType"
                [qualifier]="tableState.qualifier"
                [additionalQualifier]="tableState.additionalQualifier"
                (selectionChange)="refreshCount()"
              ></jml-lib-selection-filter>

              <!-- for any field except "isSelected display header titles and sorting icon -->
              <jml-lib-col-header
                *ngSwitchDefault
                [header]="col.header"
                [headerTooltip]="col.headerTooltip"
                [description]="col.fieldDescription"
                [sortable]="col.sortable"
                [field]="col.field"
                [cssClass]="filtersInTable ? 'pb-2' : ''"
                [sortDirection]="getSortingDefinition(col.field)?.sortDirection"
                [sortingOrder]="getSortingDefinition(col.field)?.sortingOrder"
                [showSortingOrder]="isMultiColumSort()"
                (onSort)="onSort($event, col.field)"
              ></jml-lib-col-header>
            </ng-container>

            <!-- Filters -->
            <!-- The Filters need to stop the propagation of click events. Otherwise clicking on a filter control would also result in the column being sorted. -->
            <div id="filterControl" class="table-filter" *ngIf="col.filterType !== 'none' && filtersInTable">
              <!-- text -->
              <p-columnFilter
                *ngSwitchCase="'text'"
                [field]="col.field"
                [showMenu]="false"
                [showClearButton]="false"
                (click)="$event.stopPropagation()"
              >
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <input pInputText [ngModel]="value" (ngModelChange)="filter($event)" />
                </ng-template>
              </p-columnFilter>

              <!-- integer -->
              <p-columnFilter
                *ngSwitchCase="'integer'"
                [field]="col.field"
                [showMenu]="false"
                [showClearButton]="false"
                (click)="$event.stopPropagation()"
              >
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <p-inputNumber
                    [ngModel]="value"
                    (onInput)="filter($event.value)"
                    [useGrouping]="false"
                    [maxFractionDigits]="0"
                  ></p-inputNumber>
                </ng-template>
              </p-columnFilter>

              <!-- multiSelect -->
              <p-columnFilter
                *ngSwitchCase="'multiSelect'"
                [showMenu]="false"
                [showClearButton]="false"
                [field]="col.field"
                (click)="$event.stopPropagation()"
              >
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <p-multiSelect
                    [ngModel]="value"
                    [options]="filterOptions[col.field]"
                    [filter]="_isCatalogueEntry(col)"
                    [style]="{ width: '100%' }"
                    optionLabel="name"
                    [defaultLabel]="'Auswählen..'"
                    [selectedItemsLabel]="'{0} Elemente'"
                    (onChange)="filter($event.value)"
                    dataKey="id"
                    maxSelectedLabels="1"
                    appendTo="body"
                  >
                    <ng-template let-item let-i="index" pTemplate="item">
                      <span *ngIf="!item?.icon; else displayIcon2">{{ item.name }}</span>
                      <ng-template #displayIcon2>
                        <i
                          [class]="item.icon"
                          [ngStyle]="{ color: item.color }"
                          class="multiselect-icon text-2xl mr-1"
                        ></i>
                        {{ item.name }}
                      </ng-template>
                    </ng-template>
                  </p-multiSelect>
                </ng-template>
              </p-columnFilter>

              <!-- bool -->
              <div *ngSwitchCase="'bool'" style="display: flex; justify-content: center">
                <p-columnFilter
                  type="boolean"
                  [field]="col.field"
                  class="mt-2"
                  [showClearButton]="false"
                  (click)="$event.stopPropagation()"
                ></p-columnFilter>
              </div>

              <!-- selection also renders a bool filter -->
              <div *ngSwitchCase="'selection'" style="display: flex; justify-content: center">
                <p-columnFilter
                  type="boolean"
                  [field]="col.field"
                  class="mt-2"
                  [showClearButton]="false"
                  (click)="$event.stopPropagation()"
                ></p-columnFilter>
              </div>

              <!-- date -->
              <p-columnFilter
                *ngSwitchCase="'date'"
                [field]="col.field"
                [showMenu]="false"
                [showClearButton]="false"
                (click)="$event.stopPropagation()"
              >
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <jml-lib-date-filter
                    *ngSwitchCase="'date'"
                    [ngModel]="value"
                    (ngModelChange)="filter($event)"
                    (click)="$event.stopPropagation()"
                  ></jml-lib-date-filter>
                </ng-template>
              </p-columnFilter>

              <!-- floating -->
              <p-columnFilter
                *ngSwitchCase="'floating'"
                [field]="col.field"
                [showMenu]="false"
                [showClearButton]="false"
                (click)="$event.stopPropagation()"
              >
                <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                  <jml-lib-float-filter
                    *ngSwitchCase="'floating'"
                    [ngModel]="value"
                    (ngModelChange)="filter($event)"
                    (click)="$event.stopPropagation()"
                    [header]="col.header"
                    [decimals]="col.decimals"
                  ></jml-lib-float-filter>
                </ng-template>
              </p-columnFilter>
            </div>
          </div>
        </th>

        <!-- show header buttons: refresh, add/upload, resetFilter; n/a for NoteComponent => code smell :-( -->
        <th
          [style.flex]="'0 0 ' + crudColumnSpace + 'px'"
          [style.width.px]="crudColumnSpace"
          *ngIf="tableData.showButtons"
        >
          <div class="flex justify-content-center w-full">
            <ng-container *ngTemplateOutlet="headerButtons"></ng-container>
          </div>
        </th>
      </tr>
    </ng-template>

    <!-- Processing and displaying table data -->
    <ng-template pTemplate="body" let-rowData let-fields="columns" let-index="rowIndex">
      <tr
        [pSelectableRow]="tableData.showSelectList ? null : rowData"
        (dblclick)="doubleClickTableRow($event, rowData)"
        [pReorderableRow]="index % tableState.pagination.pageSize"
      >
        <td *ngIf="tableData.showSelectList">
          <p-tableCheckbox [value]="rowData"></p-tableCheckbox>
        </td>

        <ng-template ngFor let-field [ngForOf]="fields">
          <td
            style="position: relative"
            [style.background]="rowData._color_[field.field]"
            id="hoverEffect"
            [style.min-width]="
              (inHorizontalScroll || !tableData.scrollable) && columnWidths ? columnWidths[field.field] : undefined
            "
            [style.flex]="
              !inHorizontalScroll && tableData.scrollable && columnWidths
                ? '0 0 ' + columnWidths[field.field]
                : undefined
            "
            [style.max-width]="columnWidths ? columnWidths[field.field] : undefined"
          >
            <!-- Drag and Drop if table is reorderable -->
            <ng-template #dragDropHandle>
              <div class="flex justify-content-center">
                <i class="pi pi-bars text-xl" pReorderableRowHandle></i>
              </div>
            </ng-template>

            <div
              *ngIf="field.field !== configuration.orderedColumn; else dragDropHandle"
              class="full-width-children flex align-items-center"
              [ngSwitch]="field.type"
            >
              <jml-lib-string-cell
                *ngSwitchCase="'String'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-string-cell>

              <jml-lib-integer-cell
                *ngSwitchCase="'int'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-integer-cell>

              <jml-lib-float-cell
                *ngSwitchCase="'float'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [decimals]="field.decimals"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-float-cell>

              <div *ngSwitchCase="'boolean'" style="display: flex; justify-content: center">
                <jml-lib-direct-bool-cell
                  (completeCellEdit)="completeCellEdit($event)"
                  [fieldName]="field.field"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field)"
                ></jml-lib-direct-bool-cell>
              </div>

              <div *ngSwitchCase="'Selection'" style="display: flex; justify-content: center">
                <jml-lib-direct-bool-cell
                  (completeCellEdit)="completeCellEdit($event)"
                  [fieldName]="field.field"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field)"
                  [customEdit]="!!field.editWizard"
                  (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
                ></jml-lib-direct-bool-cell>
              </div>

              <jml-lib-currency-cell
                *ngSwitchCase="'Currency'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [currency]="currency"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-currency-cell>

              <jml-lib-json-cell
                *ngSwitchCase="'json'"
                (completeCellEdit)="completeCellEdit($event)"
                [header]="configuration.singleDisplayName + ' ' + rowData['_repr_'] + ' - ' + field.header"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-json-cell>

              <jml-lib-python-cell
                *ngSwitchCase="'python'"
                (completeCellEdit)="completeCellEdit($event)"
                [header]="configuration.singleDisplayName + ' ' + rowData['_repr_'] + ' - ' + field.header"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-python-cell>

              <jml-lib-multiline-cell
                *ngSwitchCase="'Multiline'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [header]="configuration.singleDisplayName + ' ' + rowData['_repr_'] + ' - ' + field.header"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-multiline-cell>

              <jml-lib-icon-cell
                *ngSwitchCase="'Icon'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [catalogueHrid]="field.defaultCatalogue"
                [header]="field.header"
                [editable]="isInlineEditable(field, rowData.id)"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-icon-cell>

              <jml-lib-address-cell
                *ngSwitchCase="'Address'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-address-cell>

              <jml-lib-date-period-cell
                *ngSwitchCase="'DatePeriod'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [tableReference]="dynamicTableRef"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-date-period-cell>

              <jml-lib-interval-cell
                *ngSwitchCase="'DoubleRange'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-interval-cell>

              <jml-lib-calendar-week-cell
                *ngSwitchCase="'CalendarWeek'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-calendar-week-cell>

              <jml-lib-color-cell
                *ngSwitchCase="'Color'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-color-cell>

              <jml-lib-icon-picker-cell
                *ngSwitchCase="'IconDefinition'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-icon-picker-cell>

              <jml-lib-password-cell
                *ngSwitchCase="'Password'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-password-cell>

              <jml-lib-fsm-cell
                *ngSwitchCase="'State'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [entityType]="configuration.type"
                [editable]="isInlineEditable(field, rowData.id)"
                [showLabel]="field.showLabel"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-fsm-cell>

              <jml-lib-cron-cell
                *ngSwitchCase="'TriggerDefinition'"
                (completeCellEdit)="completeCellEdit($event)"
                [fieldName]="field.field"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [header]="configuration.singleDisplayName + ' ' + rowData['_repr_'] + ' - ' + field.header"
                [mandatory]="field.mandatory"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-cron-cell>

              <jml-lib-group-cell
                *ngSwitchCase="'Group'"
                [fieldName]="field.field"
                [header]="field.header"
                [mandatory]="field.mandatory"
                [editable]="isInlineEditable(field, rowData.id)"
                [relation]="field.groupType"
                [rowData]="rowData"
                (completeCellEdit)="completeCellEdit($event)"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-group-cell>

              <jml-lib-dtotype-cell
                *ngSwitchCase="'dtoType'"
                (completeCellEdit)="updateField(rowData.id, field.field, $event.value)"
                [rowData]="rowData"
                [editable]="isInlineEditable(field, rowData.id)"
                [tableReference]="dynamicTableRef"
                [field]="field"
                [customEdit]="!!field.editWizard"
                (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
              ></jml-lib-dtotype-cell>

              <ng-container *ngSwitchDefault>
                <jml-lib-entity-reference-cell
                  *ngIf="_isEntityReference(field)"
                  (completeCellEdit)="completeCellEdit($event)"
                  [field]="field"
                  [fieldName]="field.field"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field, rowData.id)"
                  [dtoType]="configuration.type"
                  [mandatory]="field.mandatory"
                  [customEdit]="!!field.editWizard"
                  (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
                ></jml-lib-entity-reference-cell>

                <jml-lib-drop-down-wrapper
                  *ngIf="_isCatalogueEntry(field)"
                  (completeCellEdit)="updateField(rowData.id, field.field, $event.value)"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field, rowData.id)"
                  [staticOptions]="filterOptions[field.field]"
                  [tableReference]="dynamicTableRef"
                  [field]="field"
                  [dtoType]="configuration.type"
                  [entityId]="rowData.id"
                  [customEdit]="!!field.editWizard"
                  (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
                ></jml-lib-drop-down-wrapper>

                <jml-lib-date-cell
                  *ngIf="field.type === 'Date' || field.type === 'DateTime'"
                  (completeCellEdit)="completeCellEdit($event)"
                  [fieldName]="field.field"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field, rowData.id)"
                  [tableReference]="dynamicTableRef"
                  [showTime]="field.type === 'DateTime'"
                  [mandatory]="field.mandatory"
                  [customEdit]="!!field.editWizard"
                  (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
                ></jml-lib-date-cell>

                <jml-lib-multiselect-wrapper
                  *ngIf="_isMultiselect(field)"
                  (completeCellEdit)="updateField(rowData.id, field.field, $event.value)"
                  [rowData]="rowData"
                  [editable]="isInlineEditable(field, rowData.id)"
                  [staticOptions]="filterOptions[field.field]"
                  [tableReference]="dynamicTableRef"
                  [field]="field"
                  [dtoType]="configuration.type"
                  [entityId]="rowData.id"
                  [customEdit]="!!field.editWizard"
                  (onCustomEdit)="executeWizard(field.editWizard, rowData.id, field.field)"
                ></jml-lib-multiselect-wrapper>
              </ng-container>
            </div>
          </td>
        </ng-template>

        <!-- Crud Buttons in last column -->
        <td
          *ngIf="tableData.showButtons && entityData"
          class="flex"
          [style.width.px]="crudColumnSpace"
          [style.min-height.em]="3.1"
          [style.align-items]="'center'"
        >
          <ng-container [ngTemplateOutlet]="crudButtons" [ngTemplateOutletContext]="{ rowData }"></ng-container>
        </td>
      </tr>
    </ng-template>
    <!-- paginator left template shows number of selected items (count from SelectionFilterComponent)     -->
    <ng-template pTemplate="paginatorleft">
      <div *ngIf="selectedCount > 0" style="width: 7rem; color: var(--text-color-secondary)">
        {{ selectedCount }} ausgewählt
      </div>
    </ng-template>
    <!-- paginator right template is only required to have the paging centered correctly -->
    <ng-template pTemplate="paginatorright">
      <div *ngIf="selectedCount > 0" style="width: 7rem; text-align: end; color: var(--text-color-secondary)"></div>
    </ng-template>
  </p-table>

  <div
    *ngIf="tableData.showAttachments && !!selectedEntityId && configuration.viewType === 'singlePage'"
    class="table-attachment-wrapper"
  >
    <jml-lib-entity-view
      [configName]="configuration.type"
      [entryId]="selectedEntityId"
      [refreshTrigger]="refreshTrigger"
    ></jml-lib-entity-view>
  </div>

  <ng-template #headerButtons>
    <ng-container
      *ngIf="
        configuration.type !== 'File' &&
        configuration.type !== 'FileAttachment' &&
        (userHasEntityCreationPermission$ | async)
      "
    >
      <p-button
        *appPermissionCheck="root.dto.$dtoType.create; varArr: ['$dtoType', configuration.type]"
        class="crud-button"
        styleClass="jml-header-button"
        (click)="createEntity()"
        icon="pi pi-plus"
        pTooltip="Hinzufügen"
      ></p-button>
    </ng-container>
    <ng-container *ngIf="configuration.type === 'File' || configuration.type === 'FileAttachment'">
      <p-button
        *appPermissionCheck="root.dto.File.upload"
        class="crud-button"
        styleClass="jml-header-button"
        (click)="uploadFile('Datei hochladen', ownerId, ownerType)"
        icon="pi pi-plus"
        pTooltip="Datei hochladen"
      ></p-button>
    </ng-container>
    <!-- TODO: Remove LogEntry Exception -->
    <ng-container *ngIf="!mainId && configuration.type !== 'LogEntry'">
      <p-button
        *appPermissionCheck="root.dto.$dtoType.export; varArr: ['$dtoType', configuration.type]"
        class="crud-button"
        styleClass="jml-header-button"
        type="button"
        icon="pi pi-file-excel"
        pTooltip="Exportieren"
        fileSaver
        (debounceClick)="exportTable('csv')"
        jmlLibDebounceDblClick
      ></p-button>
    </ng-container>
    <p-button
      class="crud-button"
      styleClass="jml-header-button"
      (click)="refreshTableContents($event)"
      icon="pi pi-refresh"
      pTooltip="Ansicht aktualisieren"
    ></p-button>
    <p-button
      *ngIf="!!preselectionId"
      class="crud-button"
      styleClass="jml-header-button"
      (click)="resetFilter($event)"
      icon="pi pi-times-circle"
      pTooltip="Filter zurücksetzen"
    ></p-button>
  </ng-template>
</ng-template>

<ng-template #crudButtons let-rowData="rowData">
  <ng-container *ngFor="let action of rowData._actions_" [ngSwitch]="action" class="html">
    <!-- hard coded actions -->
    <ng-container *ngSwitchCase="'UPDATE'">
      <p-button
        *ngIf="editableFields.length > 0"
        styleClass="jml-crud-button"
        icon="pi pi-pencil"
        class="crud-button"
        pTooltip="Bearbeiten"
        (click)="updateEntity(rowData)"
      >
      </p-button>
    </ng-container>

    <ng-container *ngSwitchCase="'DELETE'">
      <p-button
        type="button"
        styleClass="jml-crud-button"
        icon="pi pi-trash"
        class="crud-button"
        pTooltip="Löschen"
        (click)="deleteEntity(rowData)"
      >
      </p-button>
    </ng-container>

    <ng-container *ngSwitchCase="'UPLOAD'">
      <p-button
        type="button"
        styleClass="jml-crud-button"
        icon="fa fa-upload"
        class="crud-button"
        fileSaver
        pTooltip="Aktualisieren"
        (click)="uploadFile('Datei aktualisieren', mainId, mainType, rowData['id'])"
      ></p-button>
    </ng-container>

    <ng-container *ngSwitchCase="'DOWNLOAD'">
      <p-button
        type="button"
        styleClass="jml-crud-button"
        icon="fa fa-download"
        class="crud-button"
        fileSaver
        pTooltip="Herunterladen"
        [method]="'GET'"
        [fileName]="rowData['fileName']"
        [url]="downloadUrl(rowData['id'])"
      ></p-button>
    </ng-container>

    <!-- backend only sends this string if conditions to execute this action are met, no gui checks needed -->
    <ng-container *ngSwitchCase="'ADJUST_STATE_ACTION'" [jmlVar]="{ inProgress: false }" #actionButton="jmlVar">
      <p-button
        type="button"
        styleClass="jml-crud-button"
        icon="fa fa-crosshairs"
        class="crud-button"
        pTooltip="Zustand korrigieren"
        [disabled]="actionButton.inProgress"
        (onClick)="performAction('ADJUST_STATE_ACTION', configuration.type, rowData['id'], actionButton)"
      >
      </p-button>
    </ng-container>

    <ng-container *ngSwitchDefault>
      <!-- If an unknown action is sent from backend, it is ignored -->
      <ng-container *ngIf="configuration.actions[action]" [jmlVar]="{ inProgress: false }" #actionButton="jmlVar">
        <p-button
          type="button"
          styleClass="jml-crud-button"
          [icon]="configuration.actions[action].icon"
          class="crud-button"
          [pTooltip]="configuration.actions[action].tooltip"
          [disabled]="actionButton.inProgress"
          (onClick)="performAction(configuration.actions[action].hrid, configuration.type, rowData['id'], actionButton)"
        >
        </p-button>
      </ng-container>
    </ng-container>
  </ng-container>
</ng-template>

<ng-template #hasNoPermission>
  <div class="hasNoPermission">
    <h2>ACCESS DENIED</h2>
    Required reading permission: dto.{{ configuration.type }}.read<br />
    Please contact your administrator.
  </div>
</ng-template>

<p-dialog
  [(visible)]="displayAttachmentDialog"
  [header]="attachmentDialogHeader"
  class="attachment_dialog--no_padding"
  [style]="{ width: '80vw' }"
  [modal]="true"
  [blockScroll]="true"
  [draggable]="false"
  [resizable]="false"
>
  <div *ngIf="!!configuration" class="attachment_dialog-content">
    <jml-lib-entity-view
      [configName]="configuration.type"
      [entryId]="selectedEntityDialogId"
      [refreshTrigger]="refreshTrigger"
      [isRootEntity]="isRootEntity"
    ></jml-lib-entity-view>
  </div>
</p-dialog>
